import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

/**
 * 모달 URL 컨벤션의 구현체 (ops/workflow/frontend-conventions.md §1).
 *
 * ?modal= 는 "겹쳐 쌓인 모달"까지 표현하려고 쉼표로 이은 스택이다.
 *   ?modal=share-link            → 링크 모달 하나
 *   ?modal=share-link,edit-notice → 링크 모달 위에 안내문구 모달
 * 창이 하나뿐인 옛 URL(쉼표 없음)은 원소 1개짜리 스택으로 그대로 읽히므로
 * 이미 공유된 링크는 전부 살아 있다.
 */
const readStack = (params: URLSearchParams): string[] => {
  const raw = params.get("modal");
  if (!raw) return [];
  return raw.split(",").filter(Boolean);
};

const writeStack = (params: URLSearchParams, stack: string[]) => {
  if (stack.length === 0) params.delete("modal");
  else params.set("modal", stack.join(","));
};

/** 스택 맨 위에 id 를 얹는다(이미 있으면 그대로 — 같은 모달을 두 번 쌓지 않는다). */
const pushId = (stack: string[], id: string) =>
  stack.includes(id) ? stack : [...stack, id];

/** id 와 "그 위에 쌓인 것들"을 함께 걷어낸다 — 아래 창만 닫고 위 창이 떠 있는 상태를 막는다. */
const popId = (stack: string[], id: string) => {
  const at = stack.indexOf(id);
  return at === -1 ? stack : stack.slice(0, at);
};

/**
 * 스택 **맨 아래** 창의 id — "지금 이 화면이 연 창이 무엇인가".
 *
 * `?modal=` 은 쉼표로 이은 스택이라, 그 위에 창이 하나 얹히면 값이 통째로 달라진다
 * (`customer-retailer` → `customer-retailer,sms-phone`). 화면이 `params.get("modal")` 을
 * 문자열 그대로 비교하면 **자기 창을 자기가 닫아 버린다** — 그 안에서 연 문자 창까지 함께
 * 사라져 "우클릭했는데 아무 창도 안 뜬다"가 된다(티켓 #888 에서 실제로 걸렸다).
 * 그래서 통짜 비교 대신 이 함수로 맨 아래 id 만 본다.
 */
export const baseModalId = (params: URLSearchParams): string | null =>
  readStack(params)[0] ?? null;

/**
 * useModal — 모달 열림 상태 훅.
 *
 * 사용법:
 * - useModal()                 → 로컬 state 기반 (하위 호환)
 * - useModal(true)             → 초기값 true (로컬 state)
 * - useModal("invoice-preview") → URL ?modal=invoice-preview 기반.
 *   새로고침/뒤로가기/링크 공유 가능. 다른 쿼리 파라미터는 보존됨.
 *
 * Rules of Hooks를 위해 useSearchParams와 useState는 항상 호출하고,
 * 인자 타입으로 어느 쪽 결과를 사용할지 결정합니다.
 */
export const useModal = (idOrInitial?: string | boolean) => {
  const isUrlMode = typeof idOrInitial === "string";
  const modalId = isUrlMode ? (idOrInitial as string) : "";
  const initialState = typeof idOrInitial === "boolean" ? idOrInitial : false;

  const [localOpen, setLocalOpen] = useState(initialState);
  const [searchParams, setSearchParams] = useSearchParams();

  const urlOpen = isUrlMode && readStack(searchParams).includes(modalId);
  const isOpen = isUrlMode ? urlOpen : localOpen;

  const openModal = useCallback(() => {
    if (isUrlMode) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          writeStack(next, pushId(readStack(next), modalId));
          return next;
        },
        { replace: false },
      );
    } else {
      setLocalOpen(true);
    }
  }, [isUrlMode, modalId, setSearchParams]);

  const closeModal = useCallback(() => {
    if (isUrlMode) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          writeStack(next, popId(readStack(next), modalId));
          return next;
        },
        { replace: false },
      );
    } else {
      setLocalOpen(false);
    }
  }, [isUrlMode, modalId, setSearchParams]);

  const toggleModal = useCallback(() => {
    if (isOpen) closeModal();
    else openModal();
  }, [isOpen, openModal, closeModal]);

  return useMemo(
    () => ({ isOpen, openModal, closeModal, toggleModal }),
    [isOpen, openModal, closeModal, toggleModal],
  );
};

/**
 * useUrlModal — URL로 모달 상태를 관리하면서 부속 파라미터(target id 등)를
 * 함께 읽고 쓰기 위한 헬퍼. CRM처럼 "어떤 고객의 모달인가"를 표현해야 할 때 사용.
 *
 * 예: const m = useUrlModal("customer-form", ["customerId", "mode"]);
 *     m.open({ customerId: "123", mode: "edit" });
 *     m.params.customerId
 */
export const useUrlModal = <K extends string>(
  modalId: string,
  paramKeys: readonly K[] = [] as unknown as readonly K[],
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // 호출부는 대개 배열 리터럴(["fileId"])을 넘긴다 — 매 렌더 새 배열이라
  // 배열 자체를 의존성에 쓰면 memo 가 매번 깨진다. 내용으로 고정한다.
  const keySig = paramKeys.join(",");
  const keys = useMemo(
    () => (keySig ? (keySig.split(",") as K[]) : ([] as K[])),
    [keySig],
  );

  const isOpen = readStack(searchParams).includes(modalId);

  const params = useMemo(() => {
    const out = {} as Record<K, string | null>;
    for (const key of keys) {
      out[key] = searchParams.get(key);
    }
    return out;
  }, [searchParams, keys]);

  const open = useCallback(
    (values?: Partial<Record<K, string | number | null | undefined>>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          writeStack(next, pushId(readStack(next), modalId));
          if (values) {
            for (const key of Object.keys(values) as K[]) {
              const v = values[key];
              if (v === null || v === undefined || v === "") {
                next.delete(key);
              } else {
                next.set(key, String(v));
              }
            }
          }
          return next;
        },
        { replace: false },
      );
    },
    [modalId, setSearchParams],
  );

  const close = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const stack = readStack(next);
        if (!stack.includes(modalId)) return next;
        writeStack(next, popId(stack, modalId));
        for (const key of keys) {
          next.delete(key);
        }
        return next;
      },
      { replace: false },
    );
  }, [modalId, keys, setSearchParams]);

  return { isOpen, params, open, close };
};
